import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["agentType", "betaDetails", "installUrl", "onClick", "onEmbedClose", "onInstallComplete", "onInstallError", "slug", "sourceId"];
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { useIsUngatedForDrafts } from 'ai-studio-lib/shared/hooks/is-ungated/useIsUngatedForDrafts';
import { useSendOnCopilotChannel } from 'copilot-toolkit/channel/hooks/useSendOnCopilotChannel';
import { Button } from 'foundations-components/transitional/components/Button';
import { ButtonWrapper } from 'foundations-components/transitional/components/ButtonWrapper';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { gate } from 'hub-http/gates';
import { useDetailsPageContext } from 'marketplace-ui-common/context/DetailsPageContext';
import { getAgentBuilderModernUrl, getBreezeStudioUrl, getFormattedShortlinkUrl } from 'marketplace-ui-common/utils/DetailsPageUrls';
import { trackCustomMarketplaceOfferingBehavioralEvent } from 'marketplace-ui-common/utils/TrackingUtils';
import { useCallback, useMemo, useState } from 'react';
import ToolAccessTooltip from 'ui-tool-access/ToolAccessTooltip';
import { useCreateInstanceFromTemplateByGlobalIdMutation, useGetInstancesByGlobalIdQuery } from '../../__generated__/chirp/com/hubspot/ai/agent/rpc/AiAgentInstancesChirpServiceV2.hooks';
import { useGetCustomAssistantQuery, useInstallCustomAssistantTemplateFromGlobalIdMutation } from '../../__generated__/chirp/com/hubspot/customassistants/rpc/CustomAssistantsService.hooks';
import { useListTemplateInstallsByGlobalIdQuery } from '../../__generated__/chirp/com/hubspot/customassistants/rpc/TemplateManagementAppService.hooks';
import { useSendInstallIntentLazyQuery } from '../../__generated__/chirp/com/hubspot/marketplace/installs/rpc/MarketplaceInstallsRpc-app.hooks';
import { useGetActiveToolAccessRequestForScopeQuery } from '../../__generated__/chirp/com/hubspot/tool/access/rpc/ToolAccessService.hooks';
import { JoinPublicBetaModal } from '../install/JoinPublicBetaModal';
import { useGetInstallability } from './hooks/useGetInstallability';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const BREEZE_AGENT_EDIT = 'breeze-agent-edit';
const BREEZE_AGENT_ACCESS = 'breeze-agent-access';
export function AgentCTAButton(_ref) {
  var _uninstallableReason$, _uninstallableReason$2;
  let {
      agentType,
      betaDetails,
      installUrl,
      onClick,
      onEmbedClose,
      onInstallComplete,
      onInstallError,
      slug,
      sourceId
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    appContext,
    listingId,
    name: listingName,
    published,
    productType,
    isEmbedded,
    trackInteraction,
    trackInstall,
    userInfo
  } = useDetailsPageContext();
  const [sendInstallIntent] = useSendInstallIntentLazyQuery();
  const isUngatedForDrafts = useIsUngatedForDrafts();
  const isFeaturedAgent = agentType === 'OFF_PLATFORM';
  const primaryCtaMessageLocation = isFeaturedAgent ? 'detailsPage.productDetailsCard.cta.install.FEATURED_AGENT' : 'detailsPage.productDetailsCard.cta.install.AGENT';
  const isPublicBeta = (betaDetails === null || betaDetails === void 0 ? void 0 : betaDetails.betaStage) === 'PUBLIC_BETA';
  const [isPublicBetaModalOpen, setIsPublicBetaModalOpen] = useState(false);
  const hasJoinedPublicBeta = useMemo(() => {
    if (isPublicBeta && betaDetails !== null && betaDetails !== void 0 && betaDetails.gateName) {
      var _userInfo$gates;
      // eslint-disable-next-line hubspot-dev/no-bare-gate-strings
      return userInfo === null || userInfo === void 0 || (_userInfo$gates = userInfo.gates) === null || _userInfo$gates === void 0 ? void 0 : _userInfo$gates.includes(gate(betaDetails.gateName));
    }
    return true;
  }, [isPublicBeta, betaDetails === null || betaDetails === void 0 ? void 0 : betaDetails.gateName, userInfo === null || userInfo === void 0 ? void 0 : userInfo.gates]);
  const offPlatformInstallUrl = installUrl ? getFormattedShortlinkUrl(installUrl) : installUrl;
  const {
    data: breezeAgentEditAccessRequestData,
    refetch: refetchBreezeAgentEditAccessRequest
  } = useGetActiveToolAccessRequestForScopeQuery({
    variables: {
      scopeName: 'breeze-agent-edit'
    },
    skip: appContext === 'admin-preview'
  });
  const onToolAccessRequestComplete = useCallback(() => {
    trackInteraction({
      action: 'breeze agent edit access request sent'
    });
    void refetchBreezeAgentEditAccessRequest().catch(error => {
      console.error('Error refetching breeze agent edit access request status:', error);
    });
  }, [refetchBreezeAgentEditAccessRequest, trackInteraction]);
  const launchCopilotWithCustomCopilot = useSendOnCopilotChannel('LAUNCH_WITH_CUSTOM_COPILOT');
  const {
    loading: installabilityLoading,
    installed,
    portalInstallable,
    userInstallable,
    uninstallableReason,
    missingPlanTooltip,
    isOnlyMissingPublicBetaAccessForInstall
  } = useGetInstallability({
    listingId,
    appContext,
    published,
    productType
  });
  const missingBreezeAgentAccess = (uninstallableReason === null || uninstallableReason === void 0 ? void 0 : uninstallableReason.missingUserScopes.includes(BREEZE_AGENT_ACCESS)) || (uninstallableReason === null || uninstallableReason === void 0 || (_uninstallableReason$ = uninstallableReason.productSpecificUninstallableReason) === null || _uninstallableReason$ === void 0 ? void 0 : _uninstallableReason$['@type']) === 'agent' && (uninstallableReason === null || uninstallableReason === void 0 ? void 0 : uninstallableReason.productSpecificUninstallableReason.missingAiAccessControls.length) > 0;

  // Prepare tooltip content for disabled button
  const tooltipContent = useMemo(() => {
    if (missingPlanTooltip) {
      return missingPlanTooltip;
    }
    // Default to cannot install generic text, should not reach here often
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "detailsPage.productDetailsCard.cta.uninstallableReasons.cannotInstallAgent"
    });
  }, [missingPlanTooltip]);
  const {
    data: agentInstancesData,
    loading: agentInstanceLoading,
    refetch: agentInstancesRefetch
  } = useGetInstancesByGlobalIdQuery({
    skip: !sourceId || appContext !== 'app' || agentType !== 'STUDIO',
    variables: {
      globalId: sourceId ? parseInt(sourceId, 10) : 0,
      globalIdString: sourceId !== null && sourceId !== void 0 ? sourceId : ''
    }
  });
  const agentInstance = useMemo(() => {
    if (!agentInstancesData || agentInstancesData.instances.length === 0) {
      return undefined;
    }
    return agentInstancesData.instances[0];
  }, [agentInstancesData]);

  /**
   * Determines the href for the agent CTA button based on agent type and instance data
   */
  const getAgentOpenButtonHref = useCallback(() => {
    switch (agentType) {
      case 'OFF_PLATFORM':
        return offPlatformInstallUrl;
      case 'STUDIO':
        return agentInstance !== null && agentInstance !== void 0 && agentInstance.id ? getAgentBuilderModernUrl(agentInstance.id) : getBreezeStudioUrl(agentType);
      case 'ASSISTANT':
        return undefined;
      // No href, handled by onClick
      default:
        return undefined;
    }
  }, [agentType, offPlatformInstallUrl, agentInstance === null || agentInstance === void 0 ? void 0 : agentInstance.id]);
  const {
    data: installedAssistantData,
    refetch: installedAssistantRefetch,
    loading: installedAssistantLoading
  } = useListTemplateInstallsByGlobalIdQuery({
    variables: {
      templateGlobalId: sourceId || '',
      offset: {
        offset: 0
      },
      // CHIRP pagination format
      pageLength: 10 // only need to check if at least one install exists
    },
    skip: !sourceId || appContext !== 'app' || agentType !== 'ASSISTANT'
  });
  const installedAssistant = useMemo(() => {
    if (!installedAssistantData || !installedAssistantData.results) {
      return undefined;
    }

    // If there's at least one install, return the first one
    return installedAssistantData.results.length > 0 ? installedAssistantData.results[0] : undefined;
  }, [installedAssistantData]);

  // Fetch the custom assistant details if we have an installed assistant
  const {
    data: customAssistantDetails
  } = useGetCustomAssistantQuery({
    variables: {
      id: (installedAssistant === null || installedAssistant === void 0 ? void 0 : installedAssistant.customAssistantId) || 0
    },
    skip: !(installedAssistant !== null && installedAssistant !== void 0 && installedAssistant.customAssistantId)
  });
  const [createAgentInstance, {
    loading: createAgentInstanceLoading
  }] = useCreateInstanceFromTemplateByGlobalIdMutation({
    variables: {
      globalId: sourceId ? parseInt(sourceId, 10) : 0,
      globalIdString: sourceId !== null && sourceId !== void 0 ? sourceId : '',
      desiredStatus: isUngatedForDrafts ? 'DRAFT' : 'ACTIVE'
    }
  });
  const [installAssistantFromGlobalId, {
    loading: installAssistantFromGlobalIdLoading
  }] = useInstallCustomAssistantTemplateFromGlobalIdMutation({
    variables: {
      templateGlobalId: sourceId || ''
    }
  });
  const handleAgentInstalledSuccessfully = useCallback(instanceId => {
    if (agentType === 'ASSISTANT') {
      // Refetch assistant install data after successful install
      installedAssistantRefetch().catch(error => {
        console.error(error);
      });
    }
    FloatingAlertStore.addAlert({
      maxInlineSize: 'unset',
      sticky: true,
      titleText: isPublicBetaModalOpen ?
      /*#__PURE__*/
      //show specific message if beta joined as part of install
      _jsx(FormattedJSXMessage, {
        message: "detailsPage.productDetailsCard.alerts.agentBetaJoinedAndInstalledSuccessAlert",
        options: {
          listingName
        }
      }) : listingName ? /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: "detailsPage.productDetailsCard.alerts.agentInstalledSuccessAlertTitle",
        options: {
          listingName
        }
      }) : /*#__PURE__*/_jsx(FormattedMessage, {
        message: "detailsPage.productDetailsCard.alerts.genericAgentInstalledSuccessAlertTitle"
      }),
      type: 'success',
      message: instanceId && /*#__PURE__*/_jsx(ButtonWrapper, {
        children: /*#__PURE__*/_jsx(Button, {
          href: getAgentBuilderModernUrl(instanceId),
          onClick: () => trackInteraction({
            action: 'clicked open cta button',
            subscreen: 'install success alert'
          }),
          external: true,
          size: "small",
          use: "tertiary",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "detailsPage.productDetailsCard.cta.open"
          })
        })
      })
    });
    agentInstancesRefetch().catch(error => {
      console.error('Error refetching agent data:', error);
    });
    if (onInstallComplete) {
      onInstallComplete();
    }
  }, [agentInstancesRefetch, agentType, installedAssistantRefetch, isPublicBetaModalOpen, listingName, onInstallComplete, trackInteraction]);
  const handleAgentInstallFailed = useCallback(error => {
    console.error('Error installing agent:', error);
    FloatingAlertStore.addAlert({
      titleText: isPublicBetaModalOpen ?
      /*#__PURE__*/
      //show specific message if beta joined but install failed
      _jsx(FormattedJSXMessage, {
        message: "detailsPage.productDetailsCard.alerts.agentBetaJoinedAndInstalledFailedAlert",
        options: {
          listingName
        }
      }) : listingName ? /*#__PURE__*/_jsx(FormattedMessage, {
        message: "detailsPage.productDetailsCard.alerts.agentInstalledFailedAlertTitle",
        options: {
          listingName
        }
      }) : /*#__PURE__*/_jsx(FormattedMessage, {
        message: "detailsPage.productDetailsCard.alerts.genericAgentInstalledFailedAlertTitle"
      }),
      type: 'danger'
    });
    if (onInstallError) {
      onInstallError();
    }
  }, [isPublicBetaModalOpen, listingName, onInstallError]);
  const handleInstallAgent = () => {
    if (agentType === 'ASSISTANT') {
      installAssistantFromGlobalId().then(response => {
        if (response !== null && response !== void 0 && response.data) {
          var _response$data$result;
          handleAgentInstalledSuccessfully((_response$data$result = response.data.result) === null || _response$data$result === void 0 || (_response$data$result = _response$data$result.customAssistant) === null || _response$data$result === void 0 ? void 0 : _response$data$result.id);
        }
      }).catch(error => handleAgentInstallFailed(error));
    } else {
      createAgentInstance().then(response => {
        if (response !== null && response !== void 0 && response.data) {
          var _response$data$result2;
          handleAgentInstalledSuccessfully((_response$data$result2 = response.data.result) === null || _response$data$result2 === void 0 || (_response$data$result2 = _response$data$result2.agentInstance) === null || _response$data$result2 === void 0 ? void 0 : _response$data$result2.id);
        }
      }).catch(error => {
        handleAgentInstallFailed(error);
      });
    }
  };
  const handlePostBetaJoin = () => {
    if (agentType === 'OFF_PLATFORM' && offPlatformInstallUrl) {
      window.open(offPlatformInstallUrl, '_blank', 'noopener');
    } else {
      handleInstallAgent();
    }
  };

  // Render a loading state button to prevent vulnerabilities that can be caused by assuming that a user can install
  if (createAgentInstanceLoading || installedAssistantLoading || installabilityLoading || agentInstanceLoading || installAssistantFromGlobalIdLoading) {
    return /*#__PURE__*/_jsx(LoadingButton, Object.assign({
      size: "medium",
      use: "primary",
      role: "button"
    }, rest, {
      disabled: true,
      loading: true,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: primaryCtaMessageLocation
      })
    }));
  }

  // Render an "Open" button if the agent is installed except for when the user does not have agent studio access
  if ((installed || agentInstance !== null && agentInstance !== void 0 && agentInstance.id || installedAssistant) && !missingBreezeAgentAccess) {
    return /*#__PURE__*/_jsx(Button, Object.assign({
      use: "primary",
      href: getAgentOpenButtonHref(),
      external: isEmbedded,
      onClick: () => {
        onClick === null || onClick === void 0 || onClick();
        trackInteraction({
          action: 'clicked open cta button'
        });
        if (agentType === 'ASSISTANT' && installedAssistant && customAssistantDetails !== null && customAssistantDetails !== void 0 && customAssistantDetails.customAssistant) {
          launchCopilotWithCustomCopilot({
            customAssistantType: 'chatAgent',
            customAssistant: {
              id: customAssistantDetails.customAssistant.id,
              name: customAssistantDetails.customAssistant.name || listingName || '',
              welcomeMessage: customAssistantDetails.customAssistant.description,
              iconUrl: customAssistantDetails.customAssistant.icon,
              type: 'chatAgent'
            }
          });
          if (onEmbedClose) {
            onEmbedClose();
          }
        }
      }
    }, rest, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "detailsPage.productDetailsCard.cta.open"
      })
    }));
  }

  // Render agent tool access request tooltip if user lacks edit/add access
  // Only show this if the portal CAN install (has required plan) but user lacks permission
  if (portalInstallable && uninstallableReason !== null && uninstallableReason !== void 0 && (_uninstallableReason$2 = uninstallableReason.missingUserScopes) !== null && _uninstallableReason$2 !== void 0 && _uninstallableReason$2.includes(BREEZE_AGENT_EDIT)) {
    return /*#__PURE__*/_jsx(ToolAccessTooltip, {
      scope: "breeze-agent-edit",
      onComplete: onToolAccessRequestComplete,
      children: /*#__PURE__*/_jsx(Button, Object.assign({
        use: "primary",
        "data-test-id": "disabled-install-button-with-tool-access-tooltip"
      }, rest, {
        disabled: true,
        children: (breezeAgentEditAccessRequestData === null || breezeAgentEditAccessRequestData === void 0 ? void 0 : breezeAgentEditAccessRequestData.status) === 'ACTIVE' ? /*#__PURE__*/_jsx(FormattedMessage, {
          message: "detailsPage.productDetailsCard.cta.uninstallableReasons.toolAccessRequestPending"
        }) : /*#__PURE__*/_jsx(FormattedMessage, {
          message: primaryCtaMessageLocation
        })
      }))
    });
  }

  // Render a "Join beta and add" button if the agent is installable but the user has not joined the public beta
  if (isPublicBeta && !hasJoinedPublicBeta && isOnlyMissingPublicBetaAccessForInstall) {
    var _betaDetails$rolloutI;
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(Button, Object.assign({
        use: "primary",
        onClick: () => {
          onClick === null || onClick === void 0 || onClick();
          void sendInstallIntent({
            variables: {
              slug
            }
          });
          trackInteraction({
            action: 'clicked join beta and add cta button'
          });
          setIsPublicBetaModalOpen(true);
        }
      }, rest, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `detailsPage.productDetailsCard.cta.joinBetaAndAdd.${agentType === 'ASSISTANT' ? 'ASSISTANT' : 'AGENT'}`
        })
      })), isPublicBetaModalOpen && /*#__PURE__*/_jsx(JoinPublicBetaModal, {
        agentType: agentType || '',
        title: listingName,
        onClose: () => setIsPublicBetaModalOpen(false),
        rolloutId: (_betaDetails$rolloutI = betaDetails === null || betaDetails === void 0 ? void 0 : betaDetails.rolloutId) !== null && _betaDetails$rolloutI !== void 0 ? _betaDetails$rolloutI : 0,
        onSuccessfulJoin: handlePostBetaJoin,
        skipBetaAlert: agentType !== 'OFF_PLATFORM',
        agentHubBeta: (betaDetails === null || betaDetails === void 0 ? void 0 : betaDetails.gateName) === 'Workflows:ModernEditor'
      })]
    });
  }

  // Render a disabled button if the agent is not installable
  if (!userInstallable || !portalInstallable) {
    return /*#__PURE__*/_jsx(Tooltip, {
      title: tooltipContent,
      minInlineSize: "240px",
      children: /*#__PURE__*/_jsx(Button, Object.assign({
        use: "primary",
        "data-test-id": "disabled-install-button"
      }, rest, {
        disabled: true,
        children: installed ? /*#__PURE__*/_jsx(FormattedMessage, {
          message: "detailsPage.productDetailsCard.cta.open"
        }) : /*#__PURE__*/_jsx(FormattedMessage, {
          message: primaryCtaMessageLocation
        })
      }))
    });
  }

  // Render an external link button for off-platform agents
  if (agentType === 'OFF_PLATFORM') {
    return /*#__PURE__*/_jsx(Button, Object.assign({
      href: offPlatformInstallUrl,
      external: isEmbedded,
      onClick: () => {
        onClick === null || onClick === void 0 || onClick();
        void sendInstallIntent({
          variables: {
            slug
          }
        });
        trackInteraction({
          action: 'clicked off platform Install button'
        });
        trackInstall({});
      },
      use: "primary"
    }, rest, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: primaryCtaMessageLocation
      })
    }));
  }

  // Render standard button for all other agents
  return /*#__PURE__*/_jsx(Button, Object.assign({
    use: "primary",
    onClick: () => {
      onClick === null || onClick === void 0 || onClick();
      void sendInstallIntent({
        variables: {
          slug
        }
      });
      trackInstall({});
      trackCustomMarketplaceOfferingBehavioralEvent('HEADER_CONNECT');
      handleInstallAgent();
    }
  }, rest, {
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: primaryCtaMessageLocation
    })
  }));
}